// Amplitude payload tag identifying the onboarding redesign — attached to every
// space_welcome_* event and the welcome_version user property.
export const WELCOME_VERSION = 'V3';

export const ANALYTICS_EVENTS = {
  // Lifecycle / retention
  session_started: 'session_started',
  meaningful_action: 'meaningful_action',

  // App / navigation
  app_launched: 'app_launched',

  // Events
  event_viewed: 'event_viewed',
  // Fired when a guest opens the event Preview drawer from the guest landing page.
  event_preview_opened: 'event_preview_opened',
  /**
   * Fires when a user clicks a space-navigation CTA from the event page. On the authed
   * page it's the space row / Enter Space pill / feed gate (useEnterSpace); on the guest
   * page it's the preview + hosts CTAs. The `cta_source` discriminator distinguishes the
   * surfaces so funnels can attribute the event -> space conversion to the originating
   * event. Attribution flows through session attribution (src/lib/analytics/session.ts) so
   * a downstream `space_joined` can be tied back to the event the user came from.
   */
  event_space_cta_clicked: 'event_space_cta_clicked',
  /**
   * Fires when a user successfully joins (or requests to join) a space.
   * Attribution to the originating event (when applicable) flows through
   * the base payload's `ref_event` field — populated from session
   * attribution captured from the URL (either a share link or the
   * drawer CTA's appended `?ref_event=`).
   *
   * Pairs with the backend `space_joined` server event for verification
   * and with `first_space_joined` for activation-milestone tracking.
   */
  space_joined: 'space_joined',
  /**
   * The K-factor conversion signal: a user joins an event they reached via a shared link
   * (ref_user in URL). The frontend no longer emits this — the legacy check-in flow that
   * did was retired in the event-detail cutover — so the backend is now the sole producer
   * (it fires the canonical metric from the join endpoint). Kept here for funnel docs.
   */
  event_joined_via_share: 'event_joined_via_share',
  /** Fired when a user saves / unsaves an event (carries event + parent-space context). */
  event_saved: 'event_saved',
  event_unsaved: 'event_unsaved',
  /** Fired when a user marks / unmarks going to an event (carries event + parent-space context). */
  event_going: 'event_going',
  event_ungoing: 'event_ungoing',
  /**
   * The un-Going confirmation ("Not going any more?") on the event page. Properties:
   * { event_id, registration_mode }. `shown` fires when a Going member taps Going; `dismissed` on a
   * user-driven close without confirming (Stay going, swipe, outside tap, Escape). A confirmed un-Go is
   * counted by `event_ungoing`, which fires on success only.
   */
  event_ungoing_confirm_shown: 'event_ungoing_confirm_shown',
  event_ungoing_confirm_dismissed: 'event_ungoing_confirm_dismissed',
  /**
   * Fired when someone taps an event's ticket link. Properties: { event_id, surface }, with
   * surface 'page' (the "Ticketed event" line on the event page) | 'going_sheet' (the Going
   * celebration's ticket question) | 'email' (the Going confirmation email). The client never
   * emits 'email': the email links straight to the ticket site, so OneSignal's click tracking
   * counts those clicks. All three are listed so the property's values stay in one place.
   */
  event_registration_link_clicked: 'event_registration_link_clicked',
  /**
   * The Going celebration's ticket question (TECH-575) was shown. Properties: { event_id, phase,
   * surface, source? }, with phase 'upcoming' | 'live' and surface 'event_page' | 'live_slot' |
   * 'post_auth' | 'event_post_prompt' (a Going confirmed from the TECH-456 attend prompt). `source`
   * is a post-auth Going's arrival attribution (the stored intent's ?source=), present only when
   * there is one, as on event_going_welcome_viewed, so per-source counts of post-auth attends
   * combine the two events.
   */
  event_registration_sheet_shown: 'event_registration_sheet_shown',
  /**
   * Someone answered the ticket question. Properties: { event_id, answer, phase, surface }, with
   * answer 'registered' | 'not_registered'.
   */
  event_registration_self_reported: 'event_registration_self_reported',
  /**
   * The ticket question was closed without an answer (X, swipe, outside tap, Escape, back).
   * Properties: { event_id, phase, surface }.
   */
  event_registration_sheet_dismissed: 'event_registration_sheet_dismissed',
  /** Fired when a user changes the events-list filter. Properties: { view, surface }. */
  event_filter_changed: 'event_filter_changed',
  /** Fired when a user picks / clears a space in the My Calendars avatar strip. */
  event_calendar_space_selected: 'event_calendar_space_selected',
  event_calendar_space_cleared: 'event_calendar_space_cleared',
  /**
   * Fired when a user opens the composer from an event-page entry point. `surface`
   * distinguishes the affordances ('event_page_fab' | 'event_feed_empty' |
   * 'for_you_invitation'); spread buildEventContextPayload + buildEventParentSpacePayload
   * so the event -> post funnel stays attributable. The composer's own create_post_opened
   * fires when the composer mounts, which from an Event entry is once its posting gate has opened.
   */
  event_composer_opened: 'event_composer_opened',
  /**
   * TECH-456 "Are you going?" gate in front of the composer on an Event entry. Properties:
   * `surface` (EventComposerSurface | 'deep_link'), `prompt_variant` ('not_going' | 'join_spaces'),
   * `event_tense` ('upcoming' | 'live' | 'ended': which question the prompt asked),
   * plus buildEventContextPayload + buildEventParentSpacePayload. The Going result itself still
   * fires `event_going` with `source: 'event_post_prompt'`.
   */
  event_post_prompt_viewed: 'event_post_prompt_viewed',
  event_post_prompt_going_clicked: 'event_post_prompt_going_clicked',
  event_post_prompt_dismissed: 'event_post_prompt_dismissed',

  // Acquisition / attribution
  qr_code_scanned: 'qr_code_scanned',
  /**
   * The show side of the QR channel: a QR was put on screen. `surface` (QrSurface)
   * separates the share sheet's "QR code" action from the organizer card on the
   * admin invite screen. Payload is the `entity_*` shape (`entity_type`,
   * `entity_id`, `entity_slug`) plus `qr_source`, which joins to `qr_code_scanned`'s
   * `source` at the entity-and-channel grain. Never carry the QR link itself: a
   * Space link holds a live `?invite=` token and only allowlisted properties get
   * redacted (urlSecretRedactionPlugin).
   *
   * NOT an impression count for a printed poster: nothing calls home when a human
   * looks at a wall. It counts the in-app moment the QR was displayed.
   *
   * QR deliberately fires NO `share_completed`: that event is the K-factor numerator
   * and the Sharers / Super Sharers cohort definition, and a cheap repeatable toggle
   * with no observable delivery would inflate all three.
   */
  qr_code_shown: 'qr_code_shown',
  /**
   * The QR PNG actually reached the user. Gated on `downloadInviteQr`'s boolean, so a
   * dismissed native share sheet (AbortError) fires nothing. Same payload as
   * `qr_code_shown`.
   */
  qr_code_downloaded: 'qr_code_downloaded',
  /**
   * The QR PNG could not be delivered: a canvas/compositing failure, or a native shell
   * with no `canShare({ files })`, which hard-fails today and was previously invisible.
   * `qr_code_shown`'s payload plus `error_name`, mirroring `share_failed`.
   */
  qr_code_download_failed: 'qr_code_download_failed',
  referral_link_clicked: 'referral_link_clicked',
  shared_link_opened: 'shared_link_opened', // Tracks arrivals via source=app_share

  // Discovery & content
  feed_viewed: 'feed_viewed',
  feed_scrolled: 'feed_scrolled',
  feed_sort_changed: 'feed_sort_changed',
  home_tab_changed: 'home_tab_changed',
  spaces_carousel_tap: 'spaces_carousel_tap',
  spaces_carousel_explore_tap: 'spaces_carousel_explore_tap',
  /** A Meetball admin toggled a space's or event's featured status. */
  featured_status_toggled: 'featured_status_toggled',
  post_impression: 'post_impression',
  post_viewed: 'post_viewed',
  /**
   * A signed-in viewer was shown the post page's refusal that names no Event or Space to go to
   * (PrivateQuestionSignedInPlaceholder). Guests refused a post see the sign-in screen instead,
   * which reports its own legacy event. `reason` is the 403's code. Spec:
   * docs/amplitude-client-events.md.
   */
  post_refusal_signed_in_shown: 'post_refusal_signed_in_shown',
  // The post page's refusal placeholders that name what would open the post. They sent these as
  // raw Title Case strings before the registry; each value is that string as trackEvent already
  // normalized it, so Amplitude keeps one history per event (pinned by
  // src/lib/analytics/__tests__/refusalPlaceholderEventNames.test.ts).
  /** EventCheckinRequiredPlaceholder was shown: going to the named Event would open the post. */
  authenticated_user_event_checkin_required: 'authenticated_user_event_checkin_required',
  /** Its Go to Event was tapped. */
  event_checkin_required_go_to_event_clicked: 'event_checkin_required_go_to_event_clicked',
  /** NetworkOnlyPlaceholder was shown: the post is open to its author's network only. */
  authenticated_user_network_only_access_denied: 'authenticated_user_network_only_access_denied',
  /** Its View Profile was tapped. */
  network_only_view_author_profile_clicked: 'network_only_view_author_profile_clicked',
  /** Its Go to Home was tapped. */
  network_only_go_home_clicked: 'network_only_go_home_clicked',
  /** CommunityMembershipRequiredPlaceholder was shown: joining the named Space would open the post. */
  community_membership_required_shown: 'community_membership_required_shown',
  /** Its Go to Space was tapped. */
  community_membership_required_go_clicked: 'community_membership_required_go_clicked',

  // New posts pill (Meetverse home feed)
  /** Fired once per appear cycle when the pill becomes visible. */
  pill_displayed: 'pill_displayed',
  /** Fired when the user taps the pill to acknowledge. */
  pill_tapped: 'pill_tapped',
  /** Fired when the 60s detector poll rejects (network, 5xx, schema drift). */
  pill_detector_failed: 'pill_detector_failed',
  /** Fired when refresh() rejects after the user tapped the pill. */
  pill_acknowledge_refresh_failed: 'pill_acknowledge_refresh_failed',

  // Live layer v1 (design spec §9). Every live_* event carries
  // buildEventContextPayload + buildEventParentSpacePayload + a `surface`
  // property (see LiveSurface below).
  /** Fired once per mount per surface per event when a live artifact renders. */
  live_surface_displayed: 'live_surface_displayed',
  /** Fired on a live artifact's body tap (navigates to the event page, L7;
   *  the multi-live space hero lands on the Calendar tab instead). */
  live_surface_tapped: 'live_surface_tapped',
  /** Fired on every live ✕, with `stage: 1|2` (1 = card folded to pill, 2 = gone). */
  live_surface_dismissed: 'live_surface_dismissed',
  /** Fired when a recap "Remember who you met" / `We met` CTA opens the
   *  roster drawer (Home recap, Events-tab strip, ended event cards, space cell). */
  live_recap_we_met_clicked: 'live_recap_we_met_clicked',
  /** Fired once per space visit while the live sort boost is active (§8);
   *  carries `boosted_count` = annotated posts in the loaded feed. */
  live_sort_boost_applied: 'live_sort_boost_applied',

  // Post UI / moderation
  post_author_clicked: 'post_author_clicked',
  post_report_clicked: 'post_report_clicked',
  post_report_submitted: 'post_report_submitted',
  post_edit_clicked: 'post_edit_clicked',
  post_crosspost_clicked: 'post_crosspost_clicked',
  post_crosspost_event_toggled: 'post_crosspost_event_toggled',
  post_crosspost_submit_clicked: 'post_crosspost_submit_clicked',
  post_crosspost_succeeded: 'post_crosspost_succeeded',
  post_crosspost_failed: 'post_crosspost_failed',
  post_delete_clicked: 'post_delete_clicked',
  post_delete_succeeded: 'post_delete_succeeded',
  post_delete_failed: 'post_delete_failed',
  post_resolved: 'post_resolved',
  post_resolve_failed: 'post_resolve_failed',

  // Core actions
  post_create_attempted: 'post_create_attempted',
  post_created: 'post_created',
  /**
   * A post submit that did NOT publish: a client guard rule, an early return in
   * useCreateQuestionSubmit, or a server rejection. Fired once per blocked submit through
   * useReportSubmitBlocked (src/hooks/create-question/). `reason` says which; silent on a
   * double tap while mutating and on drawer dismissal. Spec: docs/amplitude-client-events.md.
   */
  post_submit_blocked: 'post_submit_blocked',
  reaction_sent: 'reaction_sent',
  help_offered: 'help_offered',
  help_removed: 'help_removed',
  chat_initiated: 'chat_initiated',
  message_sent: 'message_sent',
  we_met_confirmed: 'we_met_confirmed',

  // Admin / ICP search
  icp_search_performed: 'icp_search_performed',
  /**
   * Settled Members-tab search, once per (space, chip, query).
   * Payload: `{ space_id, space_name, space_slug, query_length, result_count }` — never the query text.
   */
  space_members_searched: 'space_members_searched',

  // Guest-only tracking
  guest_interaction_clicked: 'guest_interaction_clicked',

  // Login prompt modal
  login_prompt_shown: 'login_prompt_shown',
  login_prompt_sign_in_clicked: 'login_prompt_sign_in_clicked',
  login_prompt_dismissed: 'login_prompt_dismissed',

  // Announcements - branded toast for news from Meetball (src/components/ui/announcement.tsx).
  // Fired by the component itself, never per call. Every event carries `announcement_id`.
  /**
   * Fires when an announcement becomes visible. Not fired while it waits behind a celebration,
   * nor when a visible one is replaced in place by the same id. A later re-show fires again.
   */
  announcement_shown: 'announcement_shown',
  /** Fires when a button is tapped, before its action runs. `cta` is 'primary' | 'secondary'. */
  announcement_cta_clicked: 'announcement_cta_clicked',
  /** Fires when an announcement closes without a button tap (swipe away, or its duration ends). */
  announcement_dismissed: 'announcement_dismissed',

  // Authentication flow milestones
  auth_callback_success: 'auth_callback_success',
  signup_flow_viewed: 'signup_flow_viewed',
  signup_step_completed: 'signup_step_completed',
  /**
   * Fired whenever the app hands a visitor to Logto. `entrypoint` names the
   * surface; the /auth single-door screen's social buttons report `auth_door`.
   * The payload deliberately carries NO `extraParams` / `directSignIn`: the
   * former holds the one-time magic-link token. Pinned by a test in
   * `src/providers/auth/__tests__/useSignInOut.test.tsx`.
   */
  auth_initiated: 'auth_initiated',
  auth_cached_session_invalid: 'auth_cached_session_invalid',

  // Magic link (passwordless email), for the /auth single-door screen.
  // `magic_link_*` is the settled naming; the spec's `link_*` names were dropped.
  // Every payload here is limited to non-identifying context (`entrypoint`; the live poll's
  // join card adds `pending_action_kind`, a kind and no identity):
  // never the email address, never the one-time token.
  /** The visitor asked for a link (initial submit AND each resend). */
  magic_link_requested: 'magic_link_requested',
  /** The check-your-email confirmation was shown. Fires regardless of how the
   *  request settled, because the screen shows it regardless (enumeration). */
  magic_link_sent_shown: 'magic_link_sent_shown',
  /** /auth was opened with `?from=expired`, i.e. the auth UI bounced a dead or
   *  spent link back here. Fires once per mount. */
  magic_link_expired_shown: 'magic_link_expired_shown',
  /** The visitor chose "Use a code instead" and went to the hosted OTP flow. */
  otp_fallback_used: 'otp_fallback_used',

  // Magic-link CONTINUATION: the door holds a `{flow_id, request_secret}` pair
  // from the 202 and waits for the link to be approved somewhere else.
  //
  // Every payload here is `{ entrypoint, flow_hash }` and nothing more.
  // `flow_hash` is the first 16 hex of SHA-256(flow_id) (`flowHash` in
  // `@lib/magicLink/continuation`), `''` when `crypto.subtle` is unavailable.
  // The flow id, the request secret, the one-time token and the address never
  // appear -- pinned by AuthDoor.test.tsx's privacy test, which greps every
  // payload for all four.
  /** The waiting screen settled on a stored pair, in the page life that sent
   *  it. Once per flow id: a resume or a reload does not fire it again. */
  magic_link_waiting_shown: 'magic_link_waiting_shown',
  /** 45s on the waiting screen with nothing back: the code fallback is offered
   *  a second time, in words. Once per flow id. */
  magic_link_nudge_shown: 'magic_link_nudge_shown',
  /** The claim succeeded and this device is redeeming the token it was handed. */
  magic_link_continuation_completed: 'magic_link_continuation_completed',
  /** Every stored pair aged past the flow TTL while the door was waiting. */
  magic_link_continuation_expired: 'magic_link_continuation_expired',
  /** There is nothing to wait on: the 202 carried no pair, the poll answered
   *  in a way that cannot be retried, or it never answered inside the door's
   *  ceiling. With no pair the screen is today's confirmation unchanged; with
   *  a pair in hand the door withdraws the cross-device promise in words, so
   *  `flow_hash` separates the two on this event. */
  magic_link_continuation_unavailable: 'magic_link_continuation_unavailable',
  /** Another tab in this browser CLAIMED a flow this door has held in this
   *  page life (spent its token and started redeeming it). Fires for any such
   *  flow, whether or not it is the one on screen: the door shows "Your other
   *  tab is signing you in." only when it is. It does not mean that tab signed
   *  in: the broadcast is sent once that tab's `claimedAt` mark has landed, one
   *  statement before the redemption. Once per flow id. */
  magic_link_claimed_elsewhere: 'magic_link_claimed_elsewhere',
  /**
   * A stored pair was already approved when the app came back, so the
   * continuation provider sent the visitor to the door to finish the sign-in.
   * Payload is `{ flow_hash, trigger: 'mount' | 'foreground' }`. Registered
   * with the door's router-state entry, fired by the provider.
   */
  magic_link_continuation_resumed: 'magic_link_continuation_resumed',
  /**
   * The /auth/link approval screen (the device that HOLDS the link) rendered
   * the approve/deny choice for a visitor. Payload is `{ entrypoint,
   * flow_hash? }` and nothing else — never the address, the token, the gate,
   * or the context line (personal data about the requester).
   */
  magic_link_approval_shown: 'magic_link_approval_shown',
  /**
   * The visitor on the approval screen approved the sign-in for the OTHER
   * device. Payload is `{ entrypoint, flow_hash? }` and nothing else — never
   * the address, the token, the gate, or the context line (personal data
   * about the requester).
   */
  magic_link_approved: 'magic_link_approved',
  /**
   * The visitor chose "Continue here instead" and this device (the one that
   * HOLDS the link) claimed the token itself. Fired by the CHILD
   * (`AuthLinkApproval`) in its Continue-here handler, once (H5 ownership) —
   * never by the parent that renders it. Payload is `{ entrypoint,
   * flow_hash? }` and nothing else — never the address, the token, the gate,
   * or the context line (personal data about the requester).
   */
  magic_link_continued_here: 'magic_link_continued_here',
  /**
   * The visitor denied the sign-in ("This wasn't me"), cancelling the flow
   * for the requesting device. Payload is `{ entrypoint, flow_hash? }` and
   * nothing else — never the address, the token, the gate, or the context
   * line (personal data about the requester).
   */
  magic_link_denied: 'magic_link_denied',
  /**
   * The /auth/link landing screen decided what to show. `gate` is `confirm`,
   * `switch_account`, `same_account`, `confirm_unknown_identity` (signed in,
   * but the profile query never named the account), `identity_timeout` (the
   * profile query never settled inside IDENTITY_PENDING_TIMEOUT_MS, reported
   * apart from a genuine unknown because it is a wall-clock race and common on
   * a cold mobile load), `link_unnamed` (no usable address on the link, so it
   * is not redeemable at all) or `native` (a WebView arrival). Held back until
   * the signed-in identity has resolved except on `native` and `link_unnamed`,
   * neither of which depends on who is signed in, so `confirm` never describes
   * someone who was already signed in.
   *
   * Also carries `flow_context_source`, i.e. which copy the screen ACTED ON:
   * `server` (the link's `flow` id was exchanged for a context, which is the
   * authoritative case -- it was stored against this link's own id, and it is HELD
   * pending a click rather than stored when it names an action), `mirror` (the
   * FALLBACK: no id on the link, or the read came back with nothing, so the screen
   * acted on what this tab already held) or `none` (nothing either way). `server`
   * outranks `mirror` whenever both exist, so on a flow-bearing link a `mirror` value
   * is a read that MISSED rather than a request that was saved. The event is held
   * back for the mirror read and for that fetch, so the value describes what was
   * actually acted on; a prerendered document fires nothing until it is activated.
   *
   * And `question_claim_source` plus its boolean form `has_question_claim`, the same
   * question asked about the guest question claim. A separate label because the two
   * channels fail independently -- the backend validates each half of a context on its
   * own and drops the ones it refuses, so a `server` context can arrive with a `mirror`
   * claim beside it -- and because a dropped claim is
   * invisible in every other number: it fires no intent, so it reports
   * `pending_action_kind: null` and reads like an ordinary sign-in while the guest's
   * question is simply never published.
   *
   * Also carries `decision`: `approval` (this device does not hold the link's
   * secret; it renders the three-action approval screen) or `same_browser`
   * (it does, and gets the ordinary gate above). On an `approval` row `gate`
   * is computed from the signed-in identity as it stood BEFORE that identity
   * resolved -- arrival there is identity-irrelevant by design (see the
   * screen's header) -- and `viewedRef` latches the event once, so `gate` is
   * never recomputed or corrected after a Continue-here click swaps the
   * screen for the ordinary gate. Segment on `decision`, not on `gate`, when
   * looking at the approval population: `gate` there does not mean "the gate
   * this visitor saw."
   */
  magic_link_landing_viewed: 'magic_link_landing_viewed',
  /**
   * The visitor passed the /auth/link gate and the token went to Logto. Same
   * `gate` values as above except `native` and `link_unnamed`, which never
   * redeem, and the same rule as every event in this block: no address, no
   * token.
   *
   * AIDEV-NOTE [MEETBALL magic-link]: this does NOT always follow a human
   * activation, which is what this docstring said. `confirm` auto-proceeds with
   * no click when the arrival carries nothing pending (the compensating control
   * is the post-landing cue, not a gate -- see AuthLinkLanding's header), and so does
   * `same_account` since 2026-11-04 (product decision), whatever the arrival carries and
   * with no cue, and the
   * `auto` boolean on the payload is how the two are told apart. So `landing_viewed -> gate_confirmed` is NOT a
   * click-through rate until it is broken down by `auto`. It is also not a
   * clean ratio at all: a reveal-then-retry fires this event TWICE for one
   * link, once `auto: true` and once `auto: false`, so the numerator can exceed
   * the denominator. A reveal-then-retry adds an `auto: false` confirm per
   * retry; see `magic_link_signin_reveal_shown`.
   *
   * Also carries `pending_action_kind`: the `AuthIntent` kind waiting at the
   * moment of the redemption, or null. It is read from the stash immediately
   * after the click commits (or refuses) whatever the gate offered, which makes it
   * the record of a CHOICE -- a visitor who took "Just sign me in" reports null,
   * because that click clears the stash as well as withholding the delivery.
   * Reported for BOTH sources: a carried action is gated and named whether it came
   * from the mirror or from the server, so a non-null kind alongside `auto: true`
   * is a defect rather than a segment, EXCEPT on `gate: 'same_account'`, where `auto: true` with a
   * kind is intended (2026-11-04): the monitor is `auto = true AND gate NOT IN (confirm,
   * same_account)` at zero, and `auto = true AND kind != null AND gate != same_account` at zero.
   */
  magic_link_gate_confirmed: 'magic_link_gate_confirmed',
  /**
   * /auth/link showed an exit because a sign-in it started did not leave the
   * page. Three causes:
   * - `rejected`: the sign-in threw before navigating (the OIDC config fetch,
   *   the sign-in session write). A verdict, and the screen offers the retry.
   * - `stalled`: the sign-in resolved (settled `resolved`) but the document was
   *   still alive after `SIGN_IN_FAILURE_REVEAL_MS`. A hedge on timing, no
   *   retry -- only "Request a new link".
   * - `bfcache`: the visitor came Back from Logto into a frozen page; the
   *   retry is offered.
   * `gate` and `auto` are the values the preceding `magic_link_gate_confirmed`
   * carried for that attempt. No address, no token.
   *
   * AIDEV-NOTE [MEETBALL magic-link] (#1121): makes the 4000ms threshold
   * monitorable. Funnel caveats: docs/amplitude-client-events.md.
   */
  magic_link_signin_reveal_shown: 'magic_link_signin_reveal_shown',
  /**
   * Fired every time the SessionExpiredDrawer or its service-unavailable
   * variant is opened. Payload identifies which of the entry paths fired
   * (`reason`) plus a snapshot of the auth state machine at open time.
   * Used to triage false-positive drawer reports in production where the
   * dev-only `[drawer-open]` console log is unavailable.
   */
  auth_drawer_opened: 'auth_drawer_opened',

  // Signup onboarding (post-signup profile flow; distinct from the contextual onboarding_* events)
  signup_onboarding_started: 'signup_onboarding_started',
  signup_onboarding_professional_completed: 'signup_onboarding_professional_completed',
  signup_onboarding_interests_completed: 'signup_onboarding_interests_completed',
  signup_onboarding_completed: 'signup_onboarding_completed',
  signup_onboarding_skipped: 'signup_onboarding_skipped',

  // Chat moderation / navigation
  chat_report_clicked: 'chat_report_clicked',
  chat_report_submitted: 'chat_report_submitted',
  chat_deleted: 'chat_deleted',
  chat_user_profile_clicked: 'chat_user_profile_clicked',

  // Block / report user
  user_blocked: 'user_blocked',
  user_unblocked: 'user_unblocked',
  user_report_clicked: 'user_report_clicked',
  user_report_submitted: 'user_report_submitted',

  // Claim / moderation flows
  post_claim_clicked: 'post_claim_clicked',
  post_decline_clicked: 'post_decline_clicked',

  // Pin / unpin (space admin moderation)
  post_pin_clicked: 'post_pin_clicked',
  post_pin_confirmed: 'post_pin_confirmed',
  post_pin_succeeded: 'post_pin_succeeded',
  post_pin_failed: 'post_pin_failed',
  post_unpin_clicked: 'post_unpin_clicked',
  post_unpin_confirmed: 'post_unpin_confirmed',
  post_unpin_succeeded: 'post_unpin_succeeded',
  post_unpin_failed: 'post_unpin_failed',
  // Fired in addition to post_pin_failed when the 409 PIN_LIMIT_REACHED code is
  // returned, so product can read the cap-bump signal directly without joining
  // on error_code properties.
  pin_limit_reached: 'pin_limit_reached',

  // Hats — contextual identity per space ("Founder of Meetball", "Father of
  // Samuel"). Identity, not permission: independent of member role. Mirrors the
  // server's single ServerEvents.HAT_SET — a clear fires hat_set with
  // `cleared: true` (the backend has no hat_cleared), so client and server
  // funnels reconcile. Never attach the hat text itself; it is user-authored
  // free text. hat_length is measured on the whitespace-collapsed value, same
  // as the server, so the metric matches on both sides.
  hat_set: 'hat_set',

  // Create / edit post screen
  create_post_opened: 'create_post_opened',
  edit_post_opened: 'edit_post_opened',
  create_post_closed: 'create_post_closed',
  create_post_draft_kept: 'create_post_draft_kept',
  create_post_draft_cleared: 'create_post_draft_cleared',
  create_post_stale_draft_continued: 'create_post_stale_draft_continued',
  create_post_stale_draft_cleared: 'create_post_stale_draft_cleared',
  visibility_dropdown_opened: 'visibility_dropdown_opened',
  visibility_settings_saved: 'visibility_settings_saved',
  visibility_settings_cancelled: 'visibility_settings_cancelled',

  // Profile header UI
  profile_edit_clicked: 'profile_edit_clicked',
  settings_clicked: 'settings_clicked',
  logout_clicked: 'logout_clicked',
  referral_code_clicked: 'referral_code_clicked',

  // Profile page
  profile_viewed: 'profile_viewed',
  profile_connect_clicked: 'profile_connect_clicked',
  profile_qr_code_shown: 'profile_qr_code_shown',
  profile_connection_tags_edit_clicked: 'profile_connection_tags_edit_clicked',
  profile_view_changed: 'profile_view_changed',

  profile_linkedin_clicked: 'profile_linkedin_clicked',

  // Profile
  profile_picture_updated: 'profile_picture_updated',

  we_met_clicked: 'we_met_clicked',
  we_met_cancelled: 'we_met_cancelled',

  // We Met v2 — bidirectional connection lifecycle (TECH-295)
  we_met_request_sent: 'we_met_request_sent',
  we_met_request_confirmed: 'we_met_request_confirmed',
  we_met_request_declined: 'we_met_request_declined',
  we_met_connection_deleted: 'we_met_connection_deleted',

  note_added: 'note_added',
  note_added_failed: 'note_added_failed',

  // Onboarding / profile completion
  profile_completion_modal_shown: 'profile_completion_modal_shown',
  profile_completion_modal_clicked: 'profile_completion_modal_clicked',
  profile_completion_modal_skipped: 'profile_completion_modal_skipped',

  // Contextual onboarding
  onboarding_shown: 'onboarding_shown',
  onboarding_completed: 'onboarding_completed',
  onboarding_dismissed: 'onboarding_dismissed',
  onboarding_action_clicked: 'onboarding_action_clicked',

  // Celebration drawers (CelebrationHost) — one-shot post-signup / post-RSVP
  // moments claimed from the overlay intent channel (src/lib/overlay/intents.ts).
  // A celebration drawer was shown. The going drawer fires on every post-auth/authed
  // attend (once ever); `source` is real arrival attribution (?source= value, or null). A
  // post-auth attend on a ticketed event shows the ticket question instead, which fires
  // event_registration_sheet_shown with surface 'post_auth' and the same source.
  signup_celebration_viewed: 'signup_celebration_viewed',
  event_going_welcome_viewed: 'event_going_welcome_viewed',

  // Sharing
  share_initiated: 'share_initiated',
  share_platform_selected: 'share_platform_selected',
  share_completed: 'share_completed',
  share_failed: 'share_failed',
  share_recipient_identified: 'share_recipient_identified',
  /**
   * Post-help invite prompt (C3): fires when the post-help sheet is on screen.
   * `side` (helper|asker) + `trigger` describe the surface. Cap-limited (see
   * src/utils/invitePromptCaps.ts) so at most a few per lifetime.
   */
  share_prompt_shown: 'share_prompt_shown',
  /** Post-help invite prompt closed without sharing. Latches the caps off forever. */
  share_prompt_dismissed: 'share_prompt_dismissed',

  // App invite loop - personal /invite/{code} landing page
  /**
   * Fires once per invite-landing visit when the sharer lookup settles -
   * profile resolved, lookup failed, or no ?u param at all. `named` reports
   * whether the page greeted with the sharer's name (the settled value, never
   * a premature named:false fired while the profile was still loading).
   * Not fired for authenticated visitors - they are redirected to /home.
   */
  invite_page_viewed: 'invite_page_viewed',
  /** Fires when the invite page's signup CTA is tapped, before the auth redirect. */
  invite_page_cta_clicked: 'invite_page_cta_clicked',

  // App invite loop (C4) - dismissible invite banner on the Notifications
  // Activity tab. Only reachable for enabled viewers who have a referral code.
  /** Fires once when the invite banner becomes visible on mount. */
  invite_banner_shown: 'invite_banner_shown',
  /** Fires when the banner is tapped, opening the share sheet (variant 'banner'). */
  invite_banner_clicked: 'invite_banner_clicked',
  /** Fires when the banner is dismissed via its X control (permanent, per-user). */
  invite_banner_dismissed: 'invite_banner_dismissed',

  // Community / space — join flow funnel
  // Pairs with `space_joined` (success) to give complete funnel observability:
  //   space_join_attempted → (auth gate | space_join_routed_to_invite
  //     | pin_drawer_opened → pin_drawer_submitted)
  //     → space_joined OR space_join_failed
  // Without these, only the success leg is visible — drop-off at auth, PIN, or
  // server error is indistinguishable from "user never tapped Join."
  /** Fires when a user taps a Join CTA on a space card or community detail.
   *  Pre-auth-gate, pre-PIN-drawer — the funnel denominator. */
  space_join_attempted: 'space_join_attempted',
  /** Fires when the join mutation's onError runs. Carries `error_status`
   *  (HTTP) and `join_method` so the failure bucket is decomposable. */
  space_join_failed: 'space_join_failed',
  /** Fires when a user successfully leaves a space. Pairs with `space_joined`
   *  for cohort retention dashboards (the inverse of activation). */
  space_left: 'space_left',
  /** Fires when a host's bulk approve of PIN-holding join requests succeeds (despite the
   *  name, not on tap). Carries `surface`, `count`, `filter` + the single-Space payload. */
  bulk_approve_clicked: 'bulk_approve_clicked',
  // Space calendar sync — admin connects an external feed (Luma) in Space Setup
  // Step 4. connect/disconnect bracket the feature; sync_toggled tracks pause/resume.
  // All carry buildSingleSpacePayload(space) + `platform` + `surface: 'space_setup'`;
  // connect_failed additionally carries `error_status` (HTTP).
  /** Fires when an admin successfully connects an external calendar feed. */
  space_calendar_connected: 'space_calendar_connected',
  /** Fires when the connect mutation's onError runs (carries `error_status`). */
  space_calendar_connect_failed: 'space_calendar_connect_failed',
  /** Fires when an admin pauses/resumes syncing (carries `is_active`). */
  space_calendar_sync_toggled: 'space_calendar_sync_toggled',
  /** Fires when an admin disconnects (deletes) a calendar feed. */
  space_calendar_disconnected: 'space_calendar_disconnected',
  /** Fires when an admin's "Sync now" request is accepted (202). */
  space_calendar_synced_manually: 'space_calendar_synced_manually',
  /** Fires when the PIN-access drawer becomes visible (PIN-protected join). Create Event (TECH-851)
   *  fires it with join_surface 'create_event' when a save to a PIN main host opens the drawer. The
   *  Going PIN sheet (TECH-663) fires it once per PIN card shown, with the space triplet,
   *  join_surface 'event_going' | 'event_page_reopen' and event_id. */
  pin_drawer_opened: 'pin_drawer_opened',
  /** A PIN card of the Going PIN sheet (TECH-663) was answered. Properties: { outcome,
   *  wrong_attempts, seconds_to_resolve, event_id, space_id, join_surface }, with outcome
   *  'pin_joined' | 'pin_recorded_pending' | 'request_access' (the button, an unlisted Space, the
   *  PIN turned off) | 'host_decided' (decided elsewhere while the card was open, usually by a
   *  host) | 'dismissed' (tap away, drag, Escape or back); seconds_to_resolve counts from when that
   *  card was first shown. */
  event_space_pin_resolved: 'event_space_pin_resolved',
  /** Fires when a PIN-space Join tap is routed to the invitation accept flow instead of
   *  the PIN drawer (issue #673) — the third terminal branch of the join funnel, so the
   *  `pin_drawer_opened` / `space_join_attempted` ratio stays readable. */
  space_join_routed_to_invite: 'space_join_routed_to_invite',
  /** Fires when the user submits a PIN from the PIN-access drawer (Create Event's with join_surface
   *  'create_event'), and on each PIN sent from the Going PIN sheet (TECH-663, join_surface
   *  'event_going' | 'event_page_reopen', plus event_id). */
  pin_drawer_submitted: 'pin_drawer_submitted',
  /** Fires when the user clicks "Request access" inside the PIN drawer
   *  (no-PIN, pending-approval path). */
  pin_drawer_request_access_clicked: 'pin_drawer_request_access_clicked',
  // Migrated from raw strings — wire values intentionally unchanged so
  // existing Amplitude funnels keep working. The migration buys type safety
  // at the call site + a single source of truth for future renames.
  community_join_gate_shown: 'community_join_gate_shown',
  community_join_gate_clicked: 'community_join_gate_clicked',
  community_participate_drawer_shown: 'community_participate_drawer_shown',
  community_participate_drawer_join_clicked: 'community_participate_drawer_join_clicked',

  // Partners page of a Space (TECH-86). All three carry the same properties: space_id /
  // space_name / space_slug (buildSingleSpacePayload), listing_id, section ('perk' | 'sponsor'),
  // audience ('everyone' | 'members'), position (1-based within its section, as displayed: after
  // any search filter), surface ('search' while a query is active, else 'list') and viewer
  // ('admin' | 'member' | 'pending' | 'visitor', from the listing response). The backend keeps
  // its own per-day totals of the same three metrics (POST /spaces/{id}/partners/stats).
  /** A card at least half in view; once per card per page load (`impressions.ts`). */
  partner_listing_shown: 'partner_listing_shown',
  /** A card's detail opened. */
  partner_listing_opened: 'partner_listing_opened',
  /** "Get this perk" / "Visit {Company}" tapped in the detail. */
  partner_listing_link_clicked: 'partner_listing_link_clicked',

  // Space welcome — shareable deep-link intro/landing screen
  // (/communities/:slugOrId/welcome). All V2 funnel events carry `welcome_version`
  // (A|B) so the two signup-timing cohorts separate cleanly. Funnel:
  //   space_welcome_viewed → space_welcome_slide_viewed(1→2→3) → space_welcome_cta_clicked
  //     → space_welcome_auth_initiated → signup_onboarding_completed → join
  //   (that fourth step was `space_welcome_signup_initiated` until 2026-08-03; see its
  //    constant below for the rename and its cutover note.)
  // `source` (from ?source=) carries share-channel attribution into the funnel.
  // (Legacy V1 at /welcome-legacy also emits `skipped` + `language_toggled`.
  // V3 emits `skipped` too, from its "Continue as guest" exit; `language_toggled`
  // stays V1-only, since V3 is English-only. See below.)
  /** Fires once when the welcome screen mounts with a loaded space. */
  space_welcome_viewed: 'space_welcome_viewed',
  /** The link led nowhere: `/welcome` resolved to no space, with no error to classify, and
   *  rendered `WelcomeErrorScreen kind="notfound"` instead of the pitch. Deliberately NOT
   *  `space_welcome_viewed` — that event means the pitch was shown, and firing it here would
   *  both inflate the funnel's top and arm its once-per-slug guard with a slug-only payload.
   *  Without this constant the whole cohort was silent: no view, no slide view, no CTA click,
   *  so a printed QR for a since-removed space read as zero scans.
   *
   *  Fires once per slug, for guests AND authenticated visitors (both reach that screen —
   *  the hold sits above the returning-visitor bounce). Carries `reason`, which separates the
   *  two shapes that reach it: `'empty_response'` (a 2xx with no body — a real link that
   *  stopped resolving, and the one worth alerting on) and `'blank_slug'` (a whitespace-only
   *  route param, which never fetches at all). A GUEST's event-QR event that cannot be pitched
   *  adds two more, once per slug + event and always with `event_id`: `'event_mismatch'` (the
   *  event is not this space's) and `'event_unavailable'` (the event fetch answered not-found
   *  class or settled empty, or the event is not ACTIVE / already ended). See
   *  docs/amplitude-client-events.md. */
  space_welcome_unavailable: 'space_welcome_unavailable',
  /** Fires when the user taps the welcome screen's join CTA (pre-auth-gate). */
  space_welcome_cta_clicked: 'space_welcome_cta_clicked',
  /** User declines the welcome flow without converting. TWO producers, so charts
   *  must segment on `welcome_version` — filtering "not set" drops every V3 row:
   *
   *  - Legacy V1 (`/welcome-legacy`), dismissed via the top X — `SpaceWelcome.tsx`.
   *    Carries `language`, and NO `welcome_version`.
   *  - V3, the "Continue as guest" exit that replaced the guest-only "Sign in"
   *    button on 2026-07-30 — `screens/Onboarding/CommunityWelcomeSlide.tsx`.
   *    Carries `welcome_version: 'V3'`, no `language`. The flow's only in-band
   *    decline signal, time-locked by GUEST_UNLOCK_MS except under reduced motion
   *    or an unresolved community (see docs/amplitude-client-events.md).
   *
   *  This comment previously said V3 does not emit it and that a guest's secondary
   *  button emits `space_welcome_cta_clicked` with `cta: 'sign_in'`. Both described
   *  the interim state between the original hatch's deletion and 2026-07-30;
   *  `cta: 'sign_in'` has had no producer anywhere in `src/` since. */
  space_welcome_skipped: 'space_welcome_skipped',
  /** Legacy V1 (/welcome-legacy) only: user flips the welcome screen's EN/PT language toggle. */
  space_welcome_language_toggled: 'space_welcome_language_toggled',
  /** Fires once per slide shown on the welcome screen (slide_index 1/2/3). Carries
   *  `advance_method: 'auto' | 'manual'` (mirrors HeroSlide's own auto/button
   *  discriminator on `space_welcome_cta_clicked` above) — the carousel auto-cycles on
   *  a timer, so without this field every guest who just waits emits slide views for
   *  all three indices without swiping. That collapses `max(slide_index)` from "swipe
   *  depth" into "dwell time" and makes "joined with vs without swiping" unmeasurable;
   *  `advance_method` is what lets a query tell the two apart again. */
  space_welcome_slide_viewed: 'space_welcome_slide_viewed',
  /** Fires when the user manually taps the slide-2 "I can help" demo (not the auto-demo). */
  space_welcome_demo_interacted: 'space_welcome_demo_interacted',
  /** Fires when the welcome CTA hands a guest to Logto, immediately before the auth
   *  intent is stashed. Two producers, both in `CommunityWelcomeSlide.tsx`: the
   *  space-join arm and the event arm.
   *
   *  RENAMED from `space_welcome_signup_initiated`. The old name asserted an OUTCOME
   *  the client cannot observe: it stamps the tap, and what the tap becomes is decided
   *  on Logto's screen afterwards. `firstScreen` still defaults to 'register'
   *  (providers/auth/useSignInOut.ts), but that is a starting screen, not a result, and
   *  a returning visitor who signs in instead was always filed under "signup". Counting
   *  those as signups silently redefines the signup funnel's entry rather than splitting
   *  it, which is the case the rename policy in docs/amplitude-client-events.md calls a
   *  rename rather than a relabel.
   *
   *  Guests reaching a space WITHOUT authenticating do not appear here at all: that is
   *  the "Continue as guest" exit, which emits `space_welcome_skipped`. So the
   *  2026-07-30 guest-exit change did not widen this event's population, and the
   *  deletion of the secondary "Sign in" button narrowed it. It is now cleanly "the one
   *  CTA handed this visitor to Logto".
   *
   *  CUTOVER: no dual-emit (policy rule 2). Old rows keep the old name; `app_version`
   *  separates the bundles across the deploy window, and any query spanning it needs
   *  `event_name IN ('space_welcome_signup_initiated', 'space_welcome_auth_initiated')`.
   *  Ships in the first release cut after 2026-08-03 (dev was on 4.22.2 when the rename
   *  landed; read the exact `app_version` off the release tag before repointing charts).
   *  `welcome_version` stays 'V3' and must NOT be bumped to mark the boundary: it is
   *  also written as a sticky user property (policy rule 4). */
  space_welcome_auth_initiated: 'space_welcome_auth_initiated',

  // Event creation
  event_created: 'event_created',

  // Create event — choice drawer (import-from-Luma vs. manual)
  create_event_choice_drawer_opened: 'create_event_choice_drawer_opened',
  create_event_import_submitted: 'create_event_import_submitted',
  create_event_import_succeeded: 'create_event_import_succeeded',
  create_event_import_failed: 'create_event_import_failed',
  create_event_build_from_scratch_clicked: 'create_event_build_from_scratch_clicked',

  // Duplicate events (TECH-322): the create-form note, the import drawer and the Space review queue
  event_duplicate_shown: 'event_duplicate_shown',
  event_duplicate_action: 'event_duplicate_action',

  // Listing (TECH-855): an admin shows another Space's public event on their Space's calendar, or takes it off
  event_shown_on_space_calendar: 'event_shown_on_space_calendar',
  event_removed_from_space_calendar: 'event_removed_from_space_calendar',

  // Feed engagement
  feed_session_ended: 'feed_session_ended',

  // Create-space access gate (CreateSpace.tsx). The names predate it: they came from the
  // create-event access gate, retired in TECH-850, and stay unchanged for Amplitude funnels.
  create_event_access_referral_submitted: 'create_event_access_referral_submitted',
  create_event_access_early_access_clicked: 'create_event_access_early_access_clicked',

  // Spaces screen (Events.tsx) — migrated from raw strings. Wire values
  // intentionally unchanged so existing Amplitude funnels keep working.
  spaces_page_viewed: 'Spaces Page Viewed',
  spaces_tab_changed: 'Spaces Tab Change',
  create_event_auth_required: 'Create Event Auth Required',
  create_event_clicked: 'Create Event Clicked',

  // Token refresh coordination
  token_refresh_lock_waited: 'token_refresh_lock_waited',
  token_refresh_by_other_tab: 'token_refresh_by_other_tab',
  token_refresh_fallback_used: 'token_refresh_fallback_used',

  // Web push lifecycle incidents
  /** Fired once per session when the VAPID rotation hook exhausts its retry
   *  budget — the user is stuck with a dead subscription (no JS-only path
   *  recovers per W3C Push API InvalidStateError). Surfaced so the team
   *  can detect rotation issues in production without waiting for user reports. */
  web_push_rotation_failed: 'web_push_rotation_failed',

  // OneSignal push migration (dormant until VITE_ONESIGNAL_APP_ID is set)
  /** Fired from src/lib/onesignal.ts when OneSignal.init() rejects. Emitted at
   *  module scope via amplitude.track (not the React hook), mirroring
   *  web_vitals. Surfaces SDK/worker bootstrap failures in production so a
   *  broken OneSignal rollout is visible before it silently drops push. */
  onesignal_init_failed: 'onesignal_init_failed',
  /** Fired from useOneSignalIdentity when OneSignal.login(user.id) rejects (the
   *  external_id sync that lets a notification addressed to a Meetball user id
   *  reach the right OneSignal contact). A failure means that user won't receive
   *  OneSignal push until a later retry succeeds. Logout failures are best-effort
   *  and intentionally not tracked. */
  onesignal_login_failed: 'onesignal_login_failed',
  /** Fired once per browser from useLegacyWebPushTeardown when a device's legacy
   *  VAPID subscription is handed over to OneSignal: after OneSignal's push
   *  subscription is confirmed, the legacy backend row is deleted and the legacy
   *  browser subscription is unsubscribed. Marks a device migrating off the
   *  self-managed pipeline so it's reachable by exactly one path (no duplicate
   *  notifications during the backend's dual-send overlap). */
  web_push_migrated_to_onesignal: 'web_push_migrated_to_onesignal',
  /** Fired from useLegacyWebPushTeardown when the legacy-subscription handover
   *  fails — specifically when deleting the legacy backend row rejects, which
   *  aborts the teardown before the browser subscription is removed (backend-first
   *  ordering keeps the device retriable). Surfaces a device stuck reachable by
   *  BOTH pipelines (duplicate-notification risk) so a stalled migration is
   *  visible in production. */
  web_push_migration_teardown_failed: 'web_push_migration_teardown_failed',

  // SSE realtime connection observability
  /** Fired when the SSE connection hits a non-retriable 4xx (other than 429
   *  and the 401 retry budget) and stops reconnecting entirely. Real-time
   *  invalidation stops; EventStreamProvider surfaces a toast so the user
   *  knows to reload, and per-hook polling fallbacks (useUnreadCount,
   *  useNotificationCount, useChat, useNewMeetversePostsIndicator) re-enable
   *  when `isConnected` flips false so some surfaces continue refreshing
   *  on their own cadence. Payload carries `error_code` so production can
   *  distinguish 403 from 404 etc. */
  sse_fatal_disconnect: 'sse_fatal_disconnect',
  /** Fired when the SSE no-token retry budget exhausts and the loop pins
   *  itself at max-delay polling. A single warn log fires today; this event
   *  makes a sustained token-source regression observable in production. */
  sse_no_token_cooldown: 'sse_no_token_cooldown',
  /** Fired when an SSE event handler throws inside the broad catch around
   *  `onmessage`. Carries `event_type` so a regression in one handler doesn't
   *  silently drift the UI from server state with no production signal. */
  sse_handler_error: 'sse_handler_error',
  /** Fired when @capacitor/app's appStateChange listener registration
   *  rejects. On native, this means SSE never disconnects when the app
   *  backgrounds (battery drain rather than data loss), but still worth
   *  surfacing so the failure isn't only visible in logs. */
  sse_app_listener_failed: 'sse_app_listener_failed',
  /** Fired when the chat SSE handler can't do direct cache insertion because
   *  the payload dropped one of `messageId` / `senderId` / `content` /
   *  `createdAt`, and the handler downgrades to invalidation-only delivery. A
   *  dropped field arrives as `undefined`; only those are reported. An explicit
   *  `null` `senderId` is intentional masking (backend #525), not a drop, so a
   *  masked sender never triggers this. Surfaces backend payload regressions
   *  that would otherwise hide behind the still-working background
   *  invalidation. */
  chat_payload_partial: 'chat_payload_partial',
  /** Fired when an INTERACTION_CHANGED SSE event arrives with no questionId, so the
   *  handler can neither patch counts nor target a fallback and silently drops it.
   *  Frequent triggers signal a backend payload regression that would otherwise hide
   *  behind the (now removed) broad invalidation. */
  interaction_payload_partial: 'interaction_payload_partial',
  /** Fired when a USER_UPDATED SSE event arrives with no userId, so the handler still
   *  sweeps the identity-bearing cache families but cannot target the profile-page key
   *  or reconcile the viewer's own-identity cache. Frequent triggers signal a backend
   *  payload regression. */
  user_payload_partial: 'user_payload_partial',
  /** Fired when the new-posts pill's 250ms force-unmount fallback fires
   *  (race recovery for the "mounted at opacity-0 with no transitionend"
   *  edge case). Frequent triggers would mask a backend or SSE flap. */
  pill_stuck_mount_recovered: 'pill_stuck_mount_recovered',

  // Space activation
  space_activation_viewed: 'space_activation_viewed',
  space_activation_substep_opened: 'space_activation_substep_opened',
  space_activation_substep_completed: 'space_activation_substep_completed',
  space_activation_step_completed: 'space_activation_step_completed',
  space_activation_completed: 'space_activation_completed',
  space_activation_next_step_clicked: 'space_activation_next_step_clicked',

  // Space creation (dedicated; complements the existing meaningful_action 'space_created')
  space_created: 'space_created',

  // Notifications
  notifications_viewed: 'notifications_viewed',
  notifications_clicked: 'notifications_clicked',
  /**
   * Fired by a notification surface when the destination resolver
   * (src/lib/notifications/resolveNotificationTarget.ts) returns known:false -
   * the notification's type is missing from the vendored destination registry,
   * or its refs cannot build a path, so the tap fell back to /notifications.
   * The resolver is pure; each caller (in-app row, push tap handler, SW relay)
   * fires this exactly once per encounter site alongside a logger.error.
   * Frequent triggers mean the backend shipped a trigger_key without a webapp
   * registry re-vendor. Payload should carry the offending `type`.
   */
  notification_unknown_type: 'notification_unknown_type',

  // Bookmarks / saved
  bookmark_viewed: 'bookmark_viewed',

  // Question creation funnel
  question_type_selected: 'question_type_selected',
  question_topic_selected: 'question_topic_selected',
  question_space_selected: 'question_space_selected',

  // Suggested helpers (question-detail internal matcher)
  /** Fires once per question when the helpers section resolves to a shown result (success or empty; not on error). */
  suggestions_shown: 'suggestions_shown',
  /** Fires when a viewer taps a suggested helper to open their profile. */
  suggestion_clicked: 'suggestion_clicked',
  /** Fires when the asker taps "Introduce me" on a same-spaces helper (opens the coming-soon drawer). */
  introduce_me_clicked: 'introduce_me_clicked',
  /** Fires when a viewer expands the collapsed helpers list to reveal the remaining matches. */
  suggestions_expanded: 'suggestions_expanded',

  // Helper feed (matcher activation)
  /** Fires when the "You can help" tab becomes active. Prop `{ unseen_count }`. */
  helper_feed_viewed: 'helper_feed_viewed',
  /** Fires when a helpable card is opened. Props `{ intent, is_multi_fit, is_seen }` + question space context. */
  helper_feed_question_opened: 'helper_feed_question_opened',
  /** Fires when a helpable card is dismissed via "Not for me". Carries question space context. */
  helper_feed_question_dismissed: 'helper_feed_question_dismissed',
  /** Fires when the helper-feed empty-state primer mounts (it only mounts when shown). */
  helper_feed_profile_primer_shown: 'helper_feed_profile_primer_shown',
  /** Fires when the primer's profile-completion CTA is clicked. */
  helper_feed_profile_primer_cta_clicked: 'helper_feed_profile_primer_cta_clicked',
  /** Fires when the Activity-tab complete-profile banner becomes visible. */
  helper_feed_banner_shown: 'helper_feed_banner_shown',
  /** Fires when the banner's profile-completion CTA is clicked. */
  helper_feed_banner_cta_clicked: 'helper_feed_banner_cta_clicked',
  /** Fires when the banner is dismissed via its X control. */
  helper_feed_banner_dismissed: 'helper_feed_banner_dismissed',

  // Helpable at live events (Track 2 Phase A, spec 3.5). The three helpable
  // surfaces reuse the shared live_surface_* events with the four new `surface`
  // values below (for_you_section, for_you_card, pill_help_chip, feed_help_chip)
  // and add these two invitation-funnel events. All carry buildEventContextPayload
  // + buildEventParentSpacePayload + `helpable_count`.
  /** Fires once per mount of the "For you" invitation (0-match) empty state. */
  helpable_invitation_shown: 'helpable_invitation_shown',
  /** Fires when an invitation CTA is tapped. Prop `{ cta: 'post' | 'profile' }`. */
  helpable_invitation_cta_tapped: 'helpable_invitation_cta_tapped',
  /** Fires when the viewer expands the "For you" footer to reveal the overflow
   *  rows. Carries `surface: 'for_you_section'` + `helpable_count` + event context. */
  live_for_you_expanded: 'live_for_you_expanded',

  /**
   * Fires when a host opens the Activation Kit from the app. Props
   * `{ spaceSlug, source: 'activation_tab' | 'activation_kit_drawer' | 'publish_celebration' | 'event_page', event_id? }`;
   * `event_id` is sent with `publish_celebration` (the Space calendar's post-save confirmation, TECH-852)
   * and `event_page` (the event page's Activation Kit tab: its card and checklist rows, TECH-875).
   */
  activation_kit_opened: 'activation_kit_opened',
  /**
   * Fires when a host first plays the example host intro in the page (`kind: 'inline'`) or
   * opens it on YouTube (`kind: 'youtube'`). Props `{ spaceSlug, source: 'event_day', kind }`.
   */
  activation_kit_video_opened: 'activation_kit_video_opened',

  // Organizer Activation Kit (/communities/:slug/kit; /kit/:slug redirects). Every event carries
  // `spaceSlug`. See the kit build spec §8. Answers: do organizers work down
  // the page in order, or grab the link and leave?
  /** Fires once on kit page load. Props `{ spaceSlug, mode, referrer }`. */
  kit_viewed: 'kit_viewed',
  /** Fires when the join link is copied. Highest-intent action on the page. */
  kit_link_copied: 'kit_link_copied',
  /**
   * Fires when the kit link is shared from the kit footer.
   * Props `{ method: 'share_sheet' | 'copy' }` plus `spaceSlug` and `mode`.
   * Closing the share sheet fires nothing.
   */
  kit_shared: 'kit_shared',
  /** Fires when any copy block is copied. Props `{ templateId, variant }`. */
  kit_text_copied: 'kit_text_copied',
  /** Fires when a room asset downloads. Props `{ assetType, format }`. */
  kit_asset_downloaded: 'kit_asset_downloaded',
  /** Fires when the design generator is opened (gates Phase 2 value). */
  kit_generator_opened: 'kit_generator_opened',
  /** Fires when a logo is uploaded to the generator. Props `{ fileType, sizeKb }`. */
  kit_logo_uploaded: 'kit_logo_uploaded',
  /** Fires when a step checkbox is toggled on. Prop `{ stepId }`. */
  kit_step_checked: 'kit_step_checked',
  /** Fires at scroll-depth thresholds. Prop `{ pct }` (25/50/75/100). */
  kit_scroll_depth: 'kit_scroll_depth',
  /** Fires when the host opens their Space from the phone-preview notice (to pin posts). */
  kit_space_opened: 'kit_space_opened',
  /**
   * Fires when the host switches Where should guests land? on the kit's event pages.
   * Props `{ target: 'space' | 'event', eventId }` (`eventId` null for the Space).
   */
  kit_qr_target_switched: 'kit_qr_target_switched',
  /**
   * Fires when a stage script PDF goes out. Props `{ scriptId, action }`: `download`,
   * `share` (the PDF through the share sheet) or `whatsapp` (a link to the kit).
   */
  kit_script_downloaded: 'kit_script_downloaded',
  /**
   * Fires when a host opens an example from another host. Props `{ exampleId, kind? }`:
   * 'see-more' for the index, 'host-intro' for the video, which adds `kind` ('inline' played in
   * the page, 'youtube' opened on YouTube).
   */
  kit_example_opened: 'kit_example_opened',
  /**
   * Fires when a kit section page opens. Props `{ sectionId, from: 'landing' | 'nav' | 'examples' }`.
   * `from: 'examples'` is the Examples page opening (`sectionId: 'examples'`) or one of its Do this links.
   */
  kit_section_opened: 'kit_section_opened',
  /** Fires when the "Make it yours" brand studio opens. Prop `{ from: 'landing' | 'brand-bar' | 'asset' }`. */
  kit_brand_studio_opened: 'kit_brand_studio_opened',
  /** Fires when the brand studio closes with a brand set. Props `{ hasLogo, hasBackground, colorId, logoBoxStyle, textEdited }`. */
  kit_brand_saved: 'kit_brand_saved',

  // ── Create menu (TECH-197 § What we measure) ──
  /** The create menu opened. Props `{ surface: 'space' | 'event' }` + the Space / Event context. */
  create_menu_opened: 'create_menu_opened',
  /** A create menu row was tapped. Props `{ surface, row: 'post' | 'poll' | 'add_event' | 'perk' }`. */
  create_menu_row_chosen: 'create_menu_row_chosen',

  // Generic CTA (only wired to buttons with no existing specific event)
  cta_clicked: 'cta_clicked',
} as const;

export type AnalyticsEventName = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

/**
 * Where a live-layer artifact lives (design spec §9's `surface` property).
 * Six values extend the spec's original six (implementation rulings):
 * `at_event_chip` (W2-6) — the SpaceIndicator "at {event}" live-chip tap is its
 * own surface (none of the six fit a chip riding on a feed post); `space_feed`
 * (W2-6) — `live_sort_boost_applied` fires from the space feed itself, not from
 * any artifact surface; `space_hero` (W2-8b) — the space hero avatar's live-ring
 * tap (one live event → its page, several → the Calendar tab, with
 * `live_event_count`); `events_tab_recap` (T-6, reworked recap/pill redesign §3)
 * — the Events-tab recap strip (48h; regular event cards with recap info in a
 * swipe row, hide-on-mark; distinct from `home_recap` so the two surfaces'
 * funnels stay separable); `meetverse_pill` (recap/pill
 * redesign §1) — the PUBLIC discovery pill row above the meetverse feed
 * ONLY (guests + members; the live-first discovery funnel), and
 * `meetverse_personal_pill` (Live Layer v2 item 2) — the member's OWN
 * upcoming/soon/live rail stacked ABOVE that discovery row on the meetverse
 * tab. The two split so the member rail's own-event impressions/taps never
 * inflate the discovery funnel (a member tapping their own event is not a
 * discovery conversion); `meetverse_pill` stays discovery-only, distinct from
 * both `home_pill` (the Home-tab own rail) and `meetverse_personal_pill`.
 *
 * The final four are the helpable-at-live-event surfaces (Track 2 Phase A, spec
 * 3.5): `for_you_section` - the "For you" block resolves above the event feed
 * (displayed, carries `helpable_count`); `for_you_card` - a section card's body
 * tap / ✕ dismiss (tapped/dismissed); `pill_help_chip` - the live pill's tinted
 * count chip becomes visible (displayed, alongside the pill's own impression);
 * `feed_help_chip` - the "you can help" chips appear in a loaded feed (displayed
 * once per feed visit, `helpable_count` = chips in the loaded list, the
 * `live_sort_boost_applied` aggregate grain - never one event per post).
 */
export type LiveSurface =
  | 'home_pill'
  | 'home_recap'
  | 'space_card'
  | 'space_pill'
  | 'carousel_ring'
  | 'event_card'
  | 'at_event_chip'
  | 'space_feed'
  | 'space_hero'
  | 'events_tab_recap'
  | 'meetverse_pill'
  | 'meetverse_personal_pill'
  | 'for_you_section'
  | 'for_you_card'
  | 'pill_help_chip'
  | 'feed_help_chip';

/**
 * Where a QR was put on screen, for `qr_code_shown` / `qr_code_downloaded` /
 * `qr_code_download_failed`. One event per verb with a surface discriminator
 * rather than one constant per surface, same shape as LiveSurface above.
 *
 * `share_sheet` is the peer-facing action in SocialShareSheet (events and Spaces);
 * `admin_invite` is the organizer's InviteQrCard on AdminInviteScreen. The two
 * emit the same five common fields (`surface`, `qr_source`, `entity_type`,
 * `entity_id`, `entity_slug`), and the share sheet adds `scenario`, `sharer_id`
 * and `is_own_content`, which the organizer card has no equivalent of.
 *
 * `profile_qr_code_shown` is deliberately NOT folded in here: widening that name's
 * population is a rename under the docs/amplitude-client-events.md rules and needs
 * its own cutover.
 */
export type QrSurface = 'share_sheet' | 'admin_invite';

/**
 * Where a create-event sign-in started (TECH-847). The three auth events override the
 * path-derived `context_type` with it per call; `getContextTypeFromPathname` never emits it.
 */
export type CreateEventContextType = 'space_calendar' | 'events_feed';

export type AnalyticsContextType =
  | 'feed'
  | 'question'
  | 'event'
  | 'profile'
  | 'chat'
  | 'settings'
  | 'signup'
  | 'create_post'
  | 'unknown'
  | CreateEventContextType;

export type MeaningfulActionType =
  | 'post_created'
  | 'post_viewed'
  | 'reaction_sent'
  | 'help_offered'
  | 'chat_initiated'
  | 'message_sent'
  | 'event_checkin'
  | 'we_met_confirmed'
  | 'share_completed'
  | 'space_joined'
  | 'unknown';
